import { formatDistanceToNow } from 'date-fns'
import { ChevronDown, ChevronUp, FolderOpen, Trash2 } from 'lucide-react'
import { useState } from 'react'
import type { WpArticle, WpArticleGroup } from '@/api/hooks/use-wordpress'
import {
  useCreateWpGroup,
  useDeleteWpGroup,
  useWpGroupDetail,
  useWpGroups,
} from '@/api/hooks/use-wordpress'
import { StatusBadge } from '@/components/ui/status-badge'

interface Props {
  articles: WpArticle[]
}

export function WordPressGroupsSection({ articles }: Props): React.JSX.Element {
  const { data: groupsData, isLoading } = useWpGroups()
  const createGroup = useCreateWpGroup()
  const deleteGroup = useDeleteWpGroup()
  const [showCreate, setShowCreate] = useState(false)
  const [groupName, setGroupName] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const groups = groupsData?.data ?? []

  function toggleArticle(id: string): void {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function handleCreate(): void {
    if (!groupName.trim() || selectedIds.size === 0) return
    createGroup.mutate(
      { name: groupName.trim(), article_ids: Array.from(selectedIds) },
      {
        onSuccess: () => {
          setGroupName('')
          setSelectedIds(new Set())
          setShowCreate(false)
        },
      },
    )
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-text-primary">Article Groups</h2>
        {!showCreate && articles.length > 0 && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            + Create Group
          </button>
        )}
      </div>

      {/* Create group form */}
      {showCreate && (
        <div className="rounded-xl border border-brand-200 bg-brand-50/50 p-4 space-y-3">
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="e.g. Newsletter 27/3-2026"
              className="flex-1 rounded-lg border border-surface-3 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
            <button
              type="button"
              onClick={handleCreate}
              disabled={!groupName.trim() || selectedIds.size === 0 || createGroup.isPending}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {createGroup.isPending ? 'Creating...' : `Create (${selectedIds.size})`}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="rounded-lg border border-surface-3 px-3 py-2 text-sm hover:bg-surface-1"
            >
              Cancel
            </button>
          </div>
          <p className="text-xs text-text-tertiary">
            Select articles below to include in this group.
          </p>
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {articles.map((a) => (
              <label
                key={a.id}
                className={`flex cursor-pointer items-center gap-3 rounded-lg border p-2 text-sm transition-colors ${
                  selectedIds.has(a.id)
                    ? 'border-brand-500 bg-white'
                    : 'border-transparent hover:bg-white'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(a.id)}
                  onChange={() => toggleArticle(a.id)}
                  className="accent-brand-500"
                />
                {a.cover_image_url && (
                  <img src={a.cover_image_url} alt="" className="h-8 w-8 rounded object-cover" />
                )}
                <span className="flex-1 truncate font-medium">{a.title}</span>
                <StatusBadge status={a.status === 'published' ? 'active' : 'pending'}>
                  {a.status}
                </StatusBadge>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Groups list */}
      {isLoading ? (
        <p className="text-sm text-text-tertiary">Loading groups...</p>
      ) : groups.length === 0 && !showCreate ? (
        <div className="flex items-center gap-3 rounded-lg border border-dashed border-surface-3 p-6 text-center">
          <FolderOpen className="mx-auto h-8 w-8 text-text-tertiary" />
          <div className="text-left">
            <p className="text-sm font-medium text-text-primary">No article groups yet</p>
            <p className="text-xs text-text-secondary">
              Create a group to bundle articles for a campaign.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {groups.map((g) => (
            <GroupCard key={g.id} group={g} onDelete={() => deleteGroup.mutate(g.id)} />
          ))}
        </div>
      )}
    </section>
  )
}

function GroupCard({
  group,
  onDelete,
}: {
  group: WpArticleGroup
  onDelete: () => void
}): React.JSX.Element {
  const [expanded, setExpanded] = useState(false)
  const { data: detail } = useWpGroupDetail(expanded ? group.id : '')
  const articles = detail?.articles ?? []

  return (
    <div className="rounded-lg border border-surface-3 bg-surface-0">
      <div className="flex items-center justify-between p-3">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex flex-1 items-center gap-3 text-left"
        >
          <FolderOpen className="h-5 w-5 text-brand-500" />
          <div>
            <p className="font-medium text-text-primary">{group.name}</p>
            <p className="text-xs text-text-secondary">
              {group.article_count} article{group.article_count !== 1 ? 's' : ''} ·{' '}
              {formatDistanceToNow(new Date(group.created_at), { addSuffix: true })}
            </p>
          </div>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-text-tertiary" />
          ) : (
            <ChevronDown className="h-4 w-4 text-text-tertiary" />
          )}
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded p-1 text-text-tertiary hover:text-danger"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      {expanded && (
        <div className="border-t border-surface-3 px-3 pb-3">
          {articles.length === 0 ? (
            <p className="py-3 text-center text-xs text-text-tertiary">Loading articles...</p>
          ) : (
            <div className="space-y-2 pt-2">
              {articles.map((a) => (
                <div key={a.id} className="flex items-center gap-3 rounded-lg bg-surface-1 p-2">
                  {a.cover_image_url && (
                    <img
                      src={a.cover_image_url}
                      alt=""
                      className="h-8 w-12 shrink-0 rounded object-cover"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-text-primary">{a.title}</p>
                    <p className="text-xs text-text-secondary">{a.author ?? 'Unknown'}</p>
                  </div>
                  <StatusBadge status={a.status === 'published' ? 'active' : 'pending'}>
                    {a.status}
                  </StatusBadge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
